import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Table,
  Tag,
  Space,
  Button,
  DatePicker,
  Select,
  Input,
  Row,
  Col,
  Statistic,
  message,
  Tooltip,
  Drawer,
  Descriptions,
  Badge,
} from 'antd';
import {
  ReloadOutlined,
  DownloadOutlined,
  FilterOutlined,
  SearchOutlined,
  EyeOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { auditService } from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const SEVERITY_CONFIG = {
  info: { color: 'blue', icon: <InfoCircleOutlined /> },
  warning: { color: 'orange', icon: <WarningOutlined /> },
  critical: { color: 'red', icon: <ExclamationCircleOutlined /> },
  error: { color: 'red', icon: <CloseCircleOutlined /> },
};

const CATEGORY_COLORS = {
  auth: 'green',
  user: 'blue',
  permission: 'purple',
  data: 'cyan',
  system: 'default',
  session: 'gold',
  organization: 'magenta',
};

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [selectedLog, setSelectedLog] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    event_category: null,
    event_type: null,
    severity: null,
    user_id: null,
    search: '',
    date_from: null,
    date_to: null,
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
  });

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [pagination.current, pagination.pageSize]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = {
        skip: (pagination.current - 1) * pagination.pageSize,
        limit: pagination.pageSize,
        ...filters,
      };

      // Remove null/empty values
      Object.keys(params).forEach(key => {
        if (params[key] === null || params[key] === '' || params[key] === undefined) {
          delete params[key];
        }
      });

      const data = await auditService.getLogs(params);
      setLogs(data.items || data);
      setTotal(data.total || data.length);
    } catch (error) {
      message.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await auditService.getStats(30);
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleFilter = () => {
    setPagination({ ...pagination, current: 1 });
    fetchLogs();
  };

  const handleReset = () => {
    setFilters({
      event_category: null,
      event_type: null,
      severity: null,
      user_id: null,
      search: '',
      date_from: null,
      date_to: null,
    });
    setPagination({ ...pagination, current: 1 });
    setTimeout(fetchLogs, 0);
  };

  const handleExport = async (format) => {
    try {
      await auditService.exportLogs(format, filters);
      message.success(`Exported to ${format.toUpperCase()}`);
    } catch (error) {
      message.error('Export failed');
    }
  };

  const handleDateChange = (dates) => {
    if (dates) {
      setFilters({
        ...filters,
        date_from: dates[0].toISOString(),
        date_to: dates[1].toISOString(),
      });
    } else {
      setFilters({
        ...filters,
        date_from: null,
        date_to: null,
      });
    }
  };

  const handleTableChange = (pag) => {
    setPagination({
      current: pag.current,
      pageSize: pag.pageSize,
    });
  };

  const showLogDetails = (log) => {
    setSelectedLog(log);
    setDrawerVisible(true);
  };

  const columns = [
    {
      title: 'Time',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date) => (
        <Tooltip title={new Date(date).toLocaleString()}>
          <span>{dayjs(date).format('YYYY-MM-DD HH:mm:ss')}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Severity',
      dataIndex: 'event_severity',
      key: 'event_severity',
      width: 100,
      render: (severity) => {
        const config = SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.info;
        return (
          <Tag color={config.color} icon={config.icon}>
            {severity?.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: 'Category',
      dataIndex: 'event_category',
      key: 'event_category',
      width: 120,
      render: (category) => (
        <Tag color={CATEGORY_COLORS[category] || 'default'}>
          {category?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Event',
      dataIndex: 'event_type',
      key: 'event_type',
      width: 180,
      render: (type) => (
        <Text>{type?.replace(/_/g, ' ')}</Text>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'User',
      key: 'user',
      width: 150,
      render: (_, record) => (
        record.user_email ? (
          <Tooltip title={record.user_email}>
            <Text>{record.user_email.split('@')[0]}</Text>
          </Tooltip>
        ) : (
          <Text type="secondary">System</Text>
        )
      ),
    },
    {
      title: 'IP Address',
      dataIndex: 'ip_address',
      key: 'ip_address',
      width: 130,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Button
          type="text"
          icon={<EyeOutlined />}
          onClick={() => showLogDetails(record)}
        />
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>Audit Logs</Title>
          <Text type="secondary">View security and activity logs for compliance</Text>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => { fetchLogs(); fetchStats(); }}>
            Refresh
          </Button>
          <Button icon={<DownloadOutlined />} onClick={() => handleExport('csv')}>
            Export CSV
          </Button>
          <Button onClick={() => handleExport('json')}>
            Export JSON
          </Button>
        </Space>
      </div>

      {/* Stats Row */}
      {stats && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Total Events (30 days)"
                value={stats.total_events || 0}
                prefix={<InfoCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Login Events"
                value={stats.login_events || 0}
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Failed Logins"
                value={stats.failed_logins || 0}
                valueStyle={{ color: stats.failed_logins > 10 ? '#cf1322' : '#000' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Critical Events"
                value={stats.critical_events || 0}
                valueStyle={{ color: stats.critical_events > 0 ? '#cf1322' : '#000' }}
                prefix={stats.critical_events > 0 ? <WarningOutlined /> : null}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Filters */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col span={4}>
            <Select
              placeholder="Category"
              allowClear
              style={{ width: '100%' }}
              value={filters.event_category}
              onChange={(v) => setFilters({ ...filters, event_category: v })}
            >
              <Select.Option value="auth">Auth</Select.Option>
              <Select.Option value="user">User</Select.Option>
              <Select.Option value="permission">Permission</Select.Option>
              <Select.Option value="data">Data</Select.Option>
              <Select.Option value="session">Session</Select.Option>
              <Select.Option value="organization">Organization</Select.Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Severity"
              allowClear
              style={{ width: '100%' }}
              value={filters.severity}
              onChange={(v) => setFilters({ ...filters, severity: v })}
            >
              <Select.Option value="info">Info</Select.Option>
              <Select.Option value="warning">Warning</Select.Option>
              <Select.Option value="critical">Critical</Select.Option>
            </Select>
          </Col>
          <Col span={6}>
            <RangePicker
              style={{ width: '100%' }}
              onChange={handleDateChange}
              value={filters.date_from ? [dayjs(filters.date_from), dayjs(filters.date_to)] : null}
            />
          </Col>
          <Col span={6}>
            <Input
              placeholder="Search description..."
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              onPressEnter={handleFilter}
            />
          </Col>
          <Col span={4}>
            <Space>
              <Button type="primary" icon={<FilterOutlined />} onClick={handleFilter}>
                Filter
              </Button>
              <Button onClick={handleReset}>Reset</Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Logs Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={logs}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            total,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} records`,
          }}
          onChange={handleTableChange}
          size="small"
        />
      </Card>

      {/* Log Details Drawer */}
      <Drawer
        title="Audit Log Details"
        placement="right"
        width={600}
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
      >
        {selectedLog && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Event ID">{selectedLog.id}</Descriptions.Item>
            <Descriptions.Item label="Timestamp">
              {new Date(selectedLog.created_at).toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label="Category">
              <Tag color={CATEGORY_COLORS[selectedLog.event_category]}>
                {selectedLog.event_category?.toUpperCase()}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Event Type">
              {selectedLog.event_type?.replace(/_/g, ' ')}
            </Descriptions.Item>
            <Descriptions.Item label="Severity">
              <Tag color={SEVERITY_CONFIG[selectedLog.event_severity]?.color}>
                {selectedLog.event_severity?.toUpperCase()}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Description">
              {selectedLog.description}
            </Descriptions.Item>
            <Descriptions.Item label="User">
              {selectedLog.user_email || 'System'}
            </Descriptions.Item>
            <Descriptions.Item label="IP Address">
              {selectedLog.ip_address || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="User Agent">
              <Text style={{ fontSize: 12 }} copyable>
                {selectedLog.user_agent || '-'}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Auth Method">
              {selectedLog.auth_method || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Session ID">
              <Text code>{selectedLog.session_id || '-'}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Resource">
              {selectedLog.resource_type ? (
                <Text>
                  {selectedLog.resource_type}
                  {selectedLog.resource_id && ` #${selectedLog.resource_id}`}
                </Text>
              ) : '-'}
            </Descriptions.Item>
            {selectedLog.old_values && (
              <Descriptions.Item label="Previous Values">
                <pre style={{ fontSize: 12, margin: 0, maxHeight: 150, overflow: 'auto' }}>
                  {JSON.stringify(selectedLog.old_values, null, 2)}
                </pre>
              </Descriptions.Item>
            )}
            {selectedLog.new_values && (
              <Descriptions.Item label="New Values">
                <pre style={{ fontSize: 12, margin: 0, maxHeight: 150, overflow: 'auto' }}>
                  {JSON.stringify(selectedLog.new_values, null, 2)}
                </pre>
              </Descriptions.Item>
            )}
            {selectedLog.status && (
              <Descriptions.Item label="Status">
                <Badge
                  status={selectedLog.status === 'success' ? 'success' : 'error'}
                  text={selectedLog.status}
                />
              </Descriptions.Item>
            )}
            {selectedLog.error_message && (
              <Descriptions.Item label="Error">
                <Text type="danger">{selectedLog.error_message}</Text>
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Drawer>
    </div>
  );
};

export default AuditLogs;
